'use client';
import { useEffect, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@bridebook/aura';
import { cva } from 'class-variance-authority';

type PopoverProps = React.ComponentProps<typeof PopoverPrimitive.Root> & {
  /**
   * If true, the popover will close when the user scrolls.
   */
  closeOnScroll?: boolean;
};

const Popover = ({ closeOnScroll = false, ...props }: PopoverProps) => {
  const { open, onOpenChange, defaultOpen, ...restProps } = props;

  // Determine if we need to use internal state for closeOnScroll functionality
  const isExternallyControlled = open !== undefined && onOpenChange !== undefined;
  const needsInternalControl = closeOnScroll && !isExternallyControlled;

  // Internal state for uncontrolled mode with closeOnScroll
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);

  // Determine the actual open state and handler to use
  const actualOpen = needsInternalControl ? internalOpen : open;
  const actualOnOpenChange = needsInternalControl ? setInternalOpen : onOpenChange;

  useEffect(() => {
    if (!closeOnScroll || !actualOpen || !actualOnOpenChange) {
      return;
    }

    const handleScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-radix-popper-content-wrapper]')) {
        return;
      }
      actualOnOpenChange(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [closeOnScroll, actualOpen, actualOnOpenChange]);

  // If closeOnScroll is enabled, we always need to use controlled mode
  if (closeOnScroll) {
    return (
      <PopoverPrimitive.Root
        data-slot="popover"
        open={actualOpen}
        onOpenChange={actualOnOpenChange}
        {...restProps}
      />
    );
  }

  // Default behavior - pass through all props unchanged
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
};

const PopoverTrigger = ({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) => (
  <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
);

const popoverContentVariants = cva('', {
  variants: {
    variant: {
      // This variant is used for pure popover content, without any additional styling
      pure: '',
      // TODO: add default styling here. To be in sync with the Design System.
      // default: 'w-fit max-w-[300px] rounded-lg bg-white p-3 shadow-md',
    },
    disabledCloseAnimation: {
      true: 'data-[state=closed]:!animate-none',
      false: '',
    },
  },
});

type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content> & {
  variant?: 'pure';
  disabledCloseAnimation?: boolean;
  children: React.ReactNode;
};

const PopoverContent = ({
  className,
  align = 'start',
  sideOffset = 4,
  variant = 'pure',
  disabledCloseAnimation = false,
  children,
  ...props
}: PopoverContentProps) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-[99999] origin-(--radix-popover-content-transform-origin) outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        popoverContentVariants({ variant, disabledCloseAnimation }),
        className,
      )}
      {...props}>
      {children}
      {/* TODO: Decide if we will ever need a popover with arrow.  */}
      {/* {arrow && <PopoverPrimitive.Arrow className={cn('fill-space', classNames?.arrow)} />} */}
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
);

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
